import { useEffect, useRef, useState } from "react";

/**
 * Measures whether a CSS-clamped text element is actually overflowing
 * (scrollHeight > clientHeight), so «عرض المزيد» buttons only appear when
 * the clamp is hiding content — never from char-length guesses.
 *
 * Measure-only-while-clamped: the effect skips measurement while `expanded`
 * is true, so `isTruncated` preserves its last clamped-state result and the
 * «عرض أقل» button stays visible while expanded.
 *
 * SSR guarantee: `isTruncated` starts `false` and the effect runs post-mount
 * only, so server HTML and the first client render are identical (button
 * absent); the button appears after mount measurement.
 *
 * Re-measures on two triggers, because a single mount-time reading lies:
 *  - `document.fonts.ready` — Cairo loads from Google Fonts with
 *    `display=swap`, so the mount measurement runs against the fallback stack
 *    ("Segoe UI"/system-ui). Fallback metrics wrap Arabic taller than Cairo,
 *    so text that fits the clamp measured as overflowing and left a dead
 *    toggle under short descriptions.
 *  - `ResizeObserver` — viewport/layout changes rewrap the text. (The font
 *    swap alone does not resize the element: `lineHeight` is unitless, so the
 *    clamped height is font-family independent — hence the separate hook.)
 */
export function useClampOverflow<T extends HTMLElement = HTMLElement>(
  expanded: boolean,
  text?: string
): { ref: React.RefObject<T>; isTruncated: boolean } {
  const ref = useRef<T>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    // Only meaningful while clamped. scrollHeight > clientHeight means the
    // clamp is hiding content.
    if (expanded) return;
    const el = ref.current;
    if (!el) return;

    let cancelled = false;
    const measure = () => {
      const node = ref.current;
      if (cancelled || !node) return;
      setIsTruncated(node.scrollHeight > node.clientHeight + 1);
    };

    measure();

    // Re-measure once the real font has swapped in. Already-resolved is fine —
    // `ready` still settles, and `measure()` short-circuits after cleanup.
    void document.fonts?.ready.then(measure).catch(() => {});

    const observer =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : undefined;
    observer?.observe(el);

    return () => {
      cancelled = true;
      observer?.disconnect();
    };
  }, [text, expanded]);

  return { ref, isTruncated };
}
